import { Action } from 'actions'
import { RequestAction, isRequestAction, isFailAction, isSuccessAction } from 'helpers/actionsHelpers'
import { Reducer, StoreSet, Collection } from 'interfaces'
import { omit } from 'lodash'
import { getItemFromCollection, setItemInCollection } from 'helpers/normalizeHelpers'
import { getNextCounterValue } from 'helpers/feedHelpers'

type FilterRequestActions<T extends Action> = T extends RequestAction ? T : never
type ApiRequestAction = FilterRequestActions<Action>

function isApiRequestAction (a: Action): a is ApiRequestAction {
  return 'meta' in a && (
    a.meta === 'request' && 'params' in a.payload
    || a.meta === 'fail' && 'params' in a.payload && 'errorCode' in a
    || a.meta === 'success' && 'params' in a.payload && 'response' in a.payload
  )
}

// Creates reducer storing StoreSet of loading ids.
// Handles RequestAction with given type.
// If meta === 'request', reducer adds id or else removes it.
export function createLoadingReducer<
  TId extends string | number,
  TAction extends ApiRequestAction,
> (
  actionType: ApiRequestAction['type'],
  getId: (a: TAction) => TId,
) {
  const isAction = (t: ApiRequestAction['type']): t is typeof actionType => t === actionType

  const loadingReducer: Reducer<StoreSet<TId>> = (prevState = {}, action) => {
    if (!isApiRequestAction(action) || !isAction(action.type)) {
      return prevState
    }

    const id = getId(action as TAction)
    return action.meta === 'request'
      ? { ...prevState, [id]: true as const }
      : omit(prevState, id)
  }

  return loadingReducer
}

export function createLoadingSingleReducer (
  actionType: ApiRequestAction['type'],
) {
  const isAction = (t: ApiRequestAction['type']): t is typeof actionType => t === actionType

  const loadingSingleReducer: Reducer<boolean> = (prevState = false, action) => {
    if (!isApiRequestAction(action) || !isAction(action.type)) {
      return prevState
    }

    return action.meta === 'request'
  }

  return loadingSingleReducer
}


type CreateToggleLikeReducerArg<TFieldsMapping extends FieldsMapping, TItem extends Item<TFieldsMapping>> = {
  getActionPayload: (action: Action) => { id: TItem[FieldsMapping['id']], toMark: boolean, count: number | null } | null
  fieldsMapping: TFieldsMapping
}

type MarkReducer<TFieldsMapping extends FieldsMapping, TItem extends Item<TFieldsMapping>> = {
  <TIdKey extends FieldsMapping['id'] & keyof TItem>(action: RequestAction, prevState: Collection<TItem, TIdKey>): Collection<TItem, TIdKey>
  toggleItemMark: (action: RequestAction, prevItem: TItem) => TItem | null
}

type FieldsMapping = {
  id: string
  isMarked: string
}
type FieldsMappingWithCount =
  & FieldsMapping
  & {
    marksCount: string
  }

type Item<TFieldsMapping extends FieldsMapping> =
  & Record<TFieldsMapping['id'], string>
  & Record<TFieldsMapping['isMarked'], boolean>
  & (TFieldsMapping extends FieldsMappingWithCount ? Record<TFieldsMapping['marksCount'], number> : {})

export function createToggleMarkReducer <TFieldsMapping extends FieldsMapping | FieldsMappingWithCount, TItem extends Item<TFieldsMapping>> (outerArg: CreateToggleLikeReducerArg<TFieldsMapping, TItem>): MarkReducer<TFieldsMapping, TItem> {
  const { getActionPayload, fieldsMapping } = outerArg

  const toggleItemMark = (action: RequestAction, prevItem: TItem): TItem | null => {
    const isRequest = isRequestAction(action)
    const isFail = isFailAction(action)
    const isSuccess = isSuccessAction(action)
    const actionPayload = getActionPayload(action)

    if (!actionPayload) {
      return null
    }

    const { toMark } = actionPayload

    if (isRequest || isFail) {
      const nextIsMarked = toMark
        ? (isFail ? false : true)
        : (isFail ? true : false)

      let nextCount: number | null = null
      let nextItem: TItem = {
        ...prevItem,
        [fieldsMapping.isMarked]: nextIsMarked,
      }
      if ('marksCount' in fieldsMapping && fieldsMapping.isMarked in prevItem) {
        const { marksCount } = fieldsMapping as FieldsMappingWithCount
        nextCount = getNextCounterValue((prevItem as Item<FieldsMappingWithCount>)[marksCount], toMark, isRequest)
        nextItem = {
          ...nextItem,
          [marksCount]: nextCount,
        }
      }

      return nextItem
    }

    if (isSuccess) {
      const { count: nextCount } = actionPayload
      if (nextCount === null) {
        return prevItem
      }

      let nextItem = prevItem
      if ('marksCount' in fieldsMapping && fieldsMapping.isMarked in prevItem) {
        const { marksCount } = fieldsMapping as FieldsMappingWithCount
        if ((prevItem as Item<FieldsMappingWithCount>)[marksCount] !== nextCount) {
          nextItem = {
            ...nextItem,
            [marksCount]: nextCount,
          }
        }
      }

      return nextItem
    }

    return prevItem
  }

  const toggleState = <TUid extends FieldsMapping['id']>(action: RequestAction, prevState: Collection<TItem, TUid>): Collection<TItem, TUid> => {
    const actionPayload = getActionPayload(action)
    if (!actionPayload) {
      return prevState
    }

    const { id } = actionPayload
    const prevItem = getItemFromCollection<TItem, TUid>(prevState, id)
    if (!prevItem) {
      return prevState
    }

    const nextItem = toggleItemMark(action, prevItem)
    if (!nextItem || nextItem === prevItem) {
      return prevState
    }

    return setItemInCollection<TItem, TUid>(prevState, nextItem, fieldsMapping.id as TUid)
  }

  toggleState.toggleItemMark = toggleItemMark

  return toggleState
}
